// src/components/layout/AppBar/NotificationMenu.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications,
  NotificationsNone,
  Info,
  Warning,
  Error,
  CheckCircle,
  Assignment,
  Schedule,
  Build,
  MarkEmailRead,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useNotificationsContext } from '../../../context/NotificationsContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';

const NotificationMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 360,
    maxHeight: 480,
    marginTop: theme.spacing(1),
  },
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const NotificationList = styled(List)(({ theme }) => ({
  maxHeight: 320,
  overflow: 'auto',
  padding: 0,
}));

const NotificationItem = styled(ListItem)(({ theme, unread, hasClickUrl }) => ({
  borderLeft: unread ? `3px solid ${theme.palette.primary.main}` : 'none',
  backgroundColor: unread ? theme.palette.action.hover : 'transparent',
  cursor: hasClickUrl ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const getNotificationIcon = (type) => {
  switch (type) {
    case 'info':
      return <Info color="info" />;
    case 'warning':
      return <Warning color="warning" />;
    case 'error':
      return <Error color="error" />;
    case 'success':
      return <CheckCircle color="success" />;
    case 'task_assigned':
      return <Assignment color="secondary" />;
    case 'task_completed':
      return <CheckCircle color="success" />;
    case 'deadline_reminder':
      return <Schedule color="warning" />;
    case 'system_update':
      return <Info color="info" />;
    case 'maintenance':
      return <Build color="warning" />;
    default:
      return <Info color="action" />;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'urgent':
      return 'error';
    case 'high':
      return 'warning';
    case 'normal':
      return 'secondary';
    case 'low':
      return 'default';
    default:
      return 'default';
  }
};

// Helper function to translate notification with fallback
const useNotificationTranslation = () => {
  const { t } = useTranslation(['notifications']);

  const translateNotification = (notification) => {
    // Prefer translation key, fallback to raw text from database
    const title = notification.title_key 
      ? t(notification.title_key, { 
          ...notification.translation_params,
          defaultValue: notification.title // Fallback if translation key not found
        })
      : notification.title;
    
    const message = notification.message_key
      ? t(notification.message_key, { 
          ...notification.translation_params,
          defaultValue: notification.message // Fallback if translation key not found
        })
      : notification.message;

    return { title, message };
  };

  return translateNotification;
};

export default function NotificationMenuExp() {
  const { t } = useTranslation(['notifications']);
  const navigate = useNavigate();
  const { clearStack } = useNavigationStack();
  const [anchorEl, setAnchorEl] = useState(null);
  const translateNotification = useNotificationTranslation();

  // Use notifications context - access all the state and methods cleanly!
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  } = useNotificationsContext();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh notifications when opening menu
    fetchNotifications();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }

    // Navigate to URL if provided
    if (notification.onclick_url) {
      clearStack();
      navigate(notification.onclick_url);
    }

    handleMenuClose();
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleViewAllNotifications = () => {
    clearStack();
    navigate('/notifications');
    handleMenuClose();
  };

  const isOpen = Boolean(anchorEl);
  
  // Sort notifications by created_at timestamp (most recent first) and filter unread
  const recentNotifications = [...notifications]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .filter((noti) => (!noti.is_read));

  // Create menu children array to avoid Fragment issues
  const menuChildren = [
    // Header
    <NotificationHeader key="header">
      <Typography variant="h6" fontWeight="bold">
        {t('notifications:menu.title')}
      </Typography>
      {unreadCount > 0 && (
        <Button
          size="small"
          onClick={handleMarkAllRead}
          startIcon={<MarkEmailRead />}
        >
          {t('notifications:menu.mark_all_read')}
        </Button>
      )}
    </NotificationHeader>
  ];

  // Content
  if (isLoading) {
    menuChildren.push(
      <Box key="loading" sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (recentNotifications.length === 0) {
    menuChildren.push(
      <Box key="empty" sx={{ p: 3, textAlign: 'center' }}>
        <NotificationsNone sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('notifications:menu.no_notifications')}
        </Typography>
      </Box>
    );
  } else {
    menuChildren.push(
      <NotificationList key="list">
        {recentNotifications.map((notification) => {
          // Translate notification with fallback to database text
          const { title, message } = translateNotification(notification);

          return (
            <NotificationItem
              key={notification.notification_id}
              button
              unread={!notification.is_read}
              hasClickUrl={!!notification.onclick_url}
              onClick={() => handleNotificationClick(notification)}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.notification_type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={notification.is_read ? 'normal' : 'medium'}>
                      {title}
                    </Typography>
                    <Chip
                      label={t(`notifications:priority.${notification.priority}`)}
                      size="small"
                      color={getPriorityColor(notification.priority)}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      <LocalizedDateDistance date={notification.created_at} />
                    </Typography>
                  </Box>
                }
              />
            </NotificationItem>
          );
        })}
      </NotificationList>
    );
  }

  // Footer (only if there are notifications)
  if (recentNotifications.length > 0) {
    menuChildren.push(
      <Divider key="divider" />,
      <Box key="footer" sx={{ p: 1 }}>
        <Button
          fullWidth
          variant="text"
          onClick={handleViewAllNotifications}
        >
          {t('notifications:menu.view_all')}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleMenuOpen}
        aria-label={t('notifications:menu.title')}
        aria-controls={isOpen ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error" 
          max={99}
          sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 15, minWidth: 15 } }}
        >
          {unreadCount > 0 ? <Notifications /> : <NotificationsNone />}
        </Badge>
      </IconButton>

      <NotificationMenu
        id="notification-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuChildren}
      </NotificationMenu>
    </>
  );
}